import React, { useState } from 'react';
import { useQuery } from 'react-query';
import {
  FiDollarSign,
  FiBriefcase,
  FiFileText,
  FiTrendingUp,
  FiRefreshCw,
  FiDownload,
  FiActivity,
  FiPieChart,
  FiBarChart2,
} from 'react-icons/fi';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import toast from 'react-hot-toast';
import { lraService } from '../../services/apiService';
import LoadingSpinner from '../../components/LoadingSpinner';
import StatCard from '../../components/StatCard';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { exportCSV, exportPDF } from '../../utils/exporters';

const COLORS = ['#1a73e8', '#22c55e', '#f59e0b', '#ef4444', '#7c3aed', '#06b6d4'];

const PERIODS = [
  { days: 7, label: 'Last 7 days' },
  { days: 30, label: 'Last 30 days' },
  { days: 90, label: 'Last 90 days' },
  { days: 180, label: 'Last 6 months' },
  { days: 365, label: 'Last year' },
];

const LRAReports = () => {
  const [days, setDays] = useState(30);
  const [exporting, setExporting] = useState(false);

  // ============ Queries ============
  const {
    data: summaryData,
    isLoading,
    isRefetching,
    refetch,
  } = useQuery(
    ['lra-reports-summary', days],
    () => lraService.getReportsSummary(days),
    { refetchOnWindowFocus: false }
  );

  const { data: topData } = useQuery(
    ['lra-reports-top-taxpayers', days],
    () => lraService.getTopTaxpayers(days, 20),
    { refetchOnWindowFocus: false }
  );

  if (isLoading) return <LoadingSpinner message="Loading reports..." />;

  const summary = summaryData?.data || {};
  const kpis = summary.kpis || {};
  const byDivision = summary.by_division || [];
  const filingsByStatus = summary.filings_by_status || [];
  const paymentMix = summary.payment_mix || [];
  const compliance = summary.compliance || {};
  const audit = summary.audit || {};
  const revenueTrend = summary.revenue_trend || [];
  const registrationTrend = summary.registration_trend || [];
  const filingsBreakdown = summary.filings_breakdown || {};
  const topTaxpayers = topData?.data || [];
  const period = summary.period || {};

  const periodLabel = `${formatDate(period.start)} — ${formatDate(period.end)}`;

  // ============ Export: CSV ============
  const handleExportTopCSV = () => {
    if (topTaxpayers.length === 0) {
      toast.error('No data to export');
      return;
    }
    exportCSV(
      topTaxpayers,
      [
        { key: 'business_name', label: 'Business' },
        { key: 'tin', label: 'TIN' },
        { key: 'dssn', label: 'DSSN' },
        { key: 'tax_division', label: 'Division' },
        { key: 'verification_status', label: 'Status' },
        { key: 'transactions', label: 'Transactions' },
        { key: 'revenue', label: 'Revenue (USD)' },
        { key: 'tax_collected', label: 'Tax Collected (USD)' },
        { key: 'filings_count', label: 'Filings' },
        { key: 'tax_filed', label: 'Tax Filed (USD)' },
      ],
      `LRA-top-taxpayers-${days}d.csv`
    );
    toast.success('CSV downloaded');
  };

  // ============ Export: PDF (Full Report) ============
  const handleExportPDF = () => {
    if (topTaxpayers.length === 0 && byDivision.length === 0) {
      toast.error('No data to export');
      return;
    }

    setExporting(true);
    try {
      exportPDF({
        title: 'National Revenue Report',
        subtitle: `Period: ${periodLabel} (${days} days)`,
        summaryRows: [
          { label: 'Total Businesses', value: kpis.total_businesses || 0 },
          {
            label: 'Total Transactions',
            value: kpis.total_transactions || 0,
          },
          {
            label: 'Total Revenue',
            value: formatCurrency(kpis.total_revenue || 0),
          },
          {
            label: 'Total Tax Collected',
            value: formatCurrency(kpis.total_tax || 0),
          },
          {
            label: 'Average Transaction',
            value: formatCurrency(kpis.avg_transaction || 0),
          },
          { label: 'Total Filings', value: kpis.total_filings || 0 },
          {
            label: 'Tax Filed',
            value: formatCurrency(kpis.total_tax_filed || 0),
          },
          {
            label: 'Compliance (Verified / Pending / Rejected)',
            value: `${compliance.verified || 0} / ${
              compliance.pending || 0
            } / ${compliance.rejected || 0}`,
          },
          {
            label: 'Audit Signals (Open / Reviewing / High Risk)',
            value: `${audit.open || 0} / ${audit.reviewing || 0} / ${
              audit.high_risk || 0
            }`,
          },
        ],
        columns: [
          { key: 'business_name', label: 'Business' },
          { key: 'tin', label: 'TIN' },
          { key: 'tax_division', label: 'Division' },
          {
            key: 'transactions',
            label: 'Txns',
            formatter: (v) => String(v || 0),
          },
          {
            key: 'revenue',
            label: 'Revenue',
            formatter: (v) => formatCurrency(v || 0),
          },
          {
            key: 'tax_collected',
            label: 'Tax',
            formatter: (v) => formatCurrency(v || 0),
          },
          {
            key: 'filings_count',
            label: 'Filings',
            formatter: (v) => String(v || 0),
          },
          {
            key: 'tax_filed',
            label: 'Tax Filed',
            formatter: (v) => formatCurrency(v || 0),
          },
        ],
        rows: topTaxpayers,
        filename: `LRA-national-report-${days}d.pdf`,
      });
      toast.success('PDF downloaded');
    } catch (err) {
      console.error(err);
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  // ============ Export: Division CSV ============
  const handleExportDivisionsCSV = () => {
    if (byDivision.length === 0) {
      toast.error('No data to export');
      return;
    }
    exportCSV(
      byDivision,
      [
        { key: 'division', label: 'Division' },
        { key: 'businesses', label: 'Businesses' },
        { key: 'transactions', label: 'Transactions' },
        { key: 'revenue', label: 'Revenue (USD)' },
        { key: 'tax', label: 'Tax Collected (USD)' },
      ],
      `LRA-revenue-by-division-${days}d.csv`
    );
    toast.success('CSV downloaded');
  };

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">National Reports</h1>
          <p className="page-subtitle">
            All-business analytics for {periodLabel}
          </p>
        </div>
        <div className="page-actions">
          <button
            className="btn btn-secondary"
            onClick={() => refetch()}
            disabled={isRefetching}
          >
            <FiRefreshCw size={18} />
            Refresh
          </button>
          <button
            className="btn btn-primary"
            onClick={handleExportPDF}
            disabled={exporting}
          >
            <FiDownload size={18} />
            {exporting ? 'Exporting...' : 'Export PDF'}
          </button>
        </div>
      </div>

      {/* Period selector */}
      <div
        style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '24px',
          background: '#f3f4f6',
          padding: '4px',
          borderRadius: '8px',
          width: 'fit-content',
          flexWrap: 'wrap',
        }}
      >
        {PERIODS.map((p) => (
          <button
            key={p.days}
            className={`btn ${days === p.days ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setDays(p.days)}
            style={{ padding: '8px 16px', fontSize: '13px' }}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* KPI cards */}
      <div className="stats-grid">
        <StatCard
          icon={<FiBriefcase size={24} />}
          value={kpis.total_businesses || 0}
          label="Registered Businesses"
          color="blue"
        />
        <StatCard
          icon={<FiDollarSign size={24} />}
          value={formatCurrency(kpis.total_revenue || 0)}
          label="Total Revenue"
          color="green"
        />
        <StatCard
          icon={<FiActivity size={24} />}
          value={kpis.total_transactions || 0}
          label="Transactions"
          color="purple"
        />
        <StatCard
          icon={<FiFileText size={24} />}
          value={formatCurrency(kpis.total_tax || 0)}
          label="Tax Collected"
          color="orange"
        />
      </div>

      {/* Second KPIs row */}
      <div className="stats-grid" style={{ marginTop: 0 }}>
        <StatCard
          icon={<FiTrendingUp size={24} />}
          value={formatCurrency(kpis.avg_transaction || 0)}
          label="Avg Transaction"
          color="blue"
        />
        <StatCard
          icon={<FiFileText size={24} />}
          value={kpis.total_filings || 0}
          label="Filings (Period)"
          color="purple"
        />
        <StatCard
          icon={<FiDollarSign size={24} />}
          value={formatCurrency(kpis.total_tax_filed || 0)}
          label="Tax Filed"
          color="green"
        />
        <StatCard
          icon={<FiFileText size={24} />}
          value={filingsBreakdown.drafts || 0}
          label="Drafts Pending"
          color="orange"
        />
      </div>

      {/* Charts row 1 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))',
          gap: '24px',
          marginTop: '24px',
        }}
      >
        {/* Revenue trend */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Revenue & Tax Trend</h2>
          </div>
          {revenueTrend.length === 0 ? (
            <div className="empty-state">
              <FiTrendingUp size={40} />
              <p>No revenue data for this period</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={revenueTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(d) => {
                    const dt = new Date(d);
                    return `${dt.getMonth() + 1}/${dt.getDate()}`;
                  }}
                  fontSize={11}
                />
                <YAxis fontSize={11} />
                <Tooltip
                  formatter={(value) => formatCurrency(value)}
                  labelFormatter={(label) => formatDate(label)}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  name="Revenue"
                  stroke="#1a73e8"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
                <Line
                  type="monotone"
                  dataKey="tax"
                  name="Tax"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Revenue by division */}
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 className="card-title">Revenue by Tax Division</h2>
            <button
              className="btn btn-secondary"
              onClick={handleExportDivisionsCSV}
              style={{ padding: '6px 12px', fontSize: '12px' }}
            >
              <FiDownload size={14} />
              CSV
            </button>
          </div>
          {byDivision.length === 0 ? (
            <div className="empty-state">
              <FiBarChart2 size={40} />
              <p>No division data</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={byDivision}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="division"
                  fontSize={11}
                  tickFormatter={(v) => (v || '').replace(/_/g, ' ')}
                />
                <YAxis fontSize={11} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Bar
                  dataKey="revenue"
                  name="Revenue"
                  fill="#1a73e8"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="tax"
                  name="Tax"
                  fill="#f59e0b"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Charts row 2 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '24px',
          marginTop: '24px',
        }}
      >
        {/* Filings by status */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Filings by Status</h2>
          </div>
          {filingsByStatus.length === 0 ? (
            <div className="empty-state">
              <FiPieChart size={40} />
              <p>No filings in this period</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={filingsByStatus.map((f) => ({
                    name: f.status,
                    value: f.count,
                  }))}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name}: ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {filingsByStatus.map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Payment mix */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Payment Method Mix</h2>
          </div>
          {paymentMix.length === 0 ? (
            <div className="empty-state">
              <FiPieChart size={40} />
              <p>No payments in this period</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={paymentMix.map((p) => ({
                    name: p.method,
                    value: p.amount,
                  }))}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name}: ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {paymentMix.map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value)} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Compliance snapshot */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Compliance Snapshot</h2>
          </div>
          <ComplianceRow
            label="Verified"
            value={compliance.verified || 0}
            color="#22c55e"
          />
          <ComplianceRow
            label="Pending"
            value={compliance.pending || 0}
            color="#f59e0b"
          />
          <ComplianceRow
            label="Rejected"
            value={compliance.rejected || 0}
            color="#ef4444"
          />
          <div style={{ borderTop: '1px solid #e5e7eb', margin: '12px 0' }} />
          <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', marginBottom: 8, fontWeight: 700 }}>
            Audit Signals
          </div>
          <ComplianceRow label="Open" value={audit.open || 0} color="#f59e0b" />
          <ComplianceRow
            label="Reviewing"
            value={audit.reviewing || 0}
            color="#1a73e8"
          />
          <ComplianceRow
            label="High Risk"
            value={audit.high_risk || 0}
            color="#dc2626"
          />
        </div>
      </div>

      {/* Top Taxpayers Table */}
      <div className="card" style={{ marginTop: '24px' }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 className="card-title">Top Taxpayers by Revenue</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn btn-secondary"
              onClick={handleExportTopCSV}
              style={{ padding: '6px 14px', fontSize: '13px' }}
            >
              <FiDownload size={14} />
              CSV
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleExportPDF}
              disabled={exporting}
              style={{ padding: '6px 14px', fontSize: '13px' }}
            >
              <FiDownload size={14} />
              PDF
            </button>
          </div>
        </div>

        {topTaxpayers.length === 0 ? (
          <div className="empty-state">
            <FiBriefcase size={40} />
            <p>No taxpayer data for this period</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th>Business</th>
                  <th>TIN</th>
                  <th>Division</th>
                  <th>Txns</th>
                  <th>Revenue</th>
                  <th>Tax Collected</th>
                  <th>Filings</th>
                  <th>Tax Filed</th>
                </tr>
              </thead>
              <tbody>
                {topTaxpayers.map((t, i) => (
                  <tr key={t.dssn}>
                    <td style={{ color: '#9ca3af', fontWeight: 600 }}>
                      {i + 1}
                    </td>
                    <td>
                      <strong>{t.business_name}</strong>
                      <div
                        style={{
                          fontSize: '11px',
                          color: '#9ca3af',
                          fontFamily: 'monospace',
                          marginTop: 2,
                        }}
                      >
                        {t.dssn}
                      </div>
                    </td>
                    <td>
                      <code style={{ fontSize: '12px' }}>
                        {t.tin || '—'}
                      </code>
                    </td>
                    <td style={{ textTransform: 'capitalize' }}>
                      {(t.tax_division || 'unclassified').replace(/_/g, ' ')}
                    </td>
                    <td>{t.transactions || 0}</td>
                    <td style={{ fontWeight: 600, color: '#166534' }}>
                      {formatCurrency(t.revenue || 0)}
                    </td>
                    <td>{formatCurrency(t.tax_collected || 0)}</td>
                    <td>
                      <span
                        style={{
                          background:
                            t.filings_count > 0 ? '#dcfce7' : '#f3f4f6',
                          color: t.filings_count > 0 ? '#166534' : '#6b7280',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 600,
                        }}
                      >
                        {t.filings_count || 0}
                      </span>
                    </td>
                    <td>{formatCurrency(t.tax_filed || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- Helper components ----------
const ComplianceRow = ({ label, value, color }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '8px 0',
    }}
  >
    <span style={{ fontSize: '13px', color: '#374151' }}>{label}</span>
    <span
      style={{
        background: `${color}20`,
        color: color,
        padding: '4px 12px',
        borderRadius: '12px',
        fontWeight: 700,
        fontSize: '14px',
      }}
    >
      {value}
    </span>
  </div>
);

export default LRAReports;
